import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { SpeciesKey } from '@chicare/shared';

import { useProductSearch } from '@/lib/queries';
import { useSector, useSectorInfo } from '@/lib/sector';
import { AppBar } from '@/components/ui/AppBar';
import { SearchField } from '@/components/ui/SearchField';
import { TrustBanner } from '@/components/ui/TrustBanner';
import { SponsoredBlock } from '@/components/catalogue/SponsoredBlock';
import { Chip, ChipRow } from '@/components/ui/Chip';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState, NoResultsArt } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/Button';
import { ProductRow } from '@/features/product/ProductCard';

/**
 * Search.
 *
 * The typo tolerance matters more here than anywhere else in the app: buyers
 * type what is printed on a pack, in a hurry, on a phone keyboard, and
 * "paracitamol" must find paracetamol. That happens server-side (Meilisearch
 * from Phase 1); this screen's job is to keep the query in the URL so a result
 * can be shared, and to never show a bare white screen while it waits.
 *
 * ── THE SEARCH IS WALLED, AND THE WALL IS THE PATH ──────────────────────────
 *
 * /search searches human medicines; /vet/search searches animal health. There
 * is no "search everything", and that is the point: "ivermectin" is a real
 * product on both sides at wildly different strengths, and a single result list
 * containing both is a dosing error with a nice interface on it.
 */
export function SearchScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sector = useSector();
  const info = useSectorInfo();
  const isVet = sector === 'veterinary';

  const query = params.get('q') ?? '';
  const category = params.get('category') ?? undefined;
  const species = (params.get('species') as SpeciesKey | null) ?? undefined;
  // `price` unless somebody chose otherwise — the same default the API uses,
  // written here too so the chip row can show which order is active on the
  // first render rather than after the response lands.
  const sort = (params.get('sort') as 'price' | 'newest' | null) ?? 'price';

  const [draft, setDraft] = useState(query);
  const debounced = useDebounced(draft, 250);

  // The URL is the source of truth, so a result is shareable and Back works.
  useEffect(() => {
    const next = new URLSearchParams(params);

    if (debounced) next.set('q', debounced);
    else next.delete('q');

    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const { data, isLoading, isFetching, isError, refetch } = useProductSearch({
    q: debounced || undefined,
    category,
    sector,
    species,
    sort,
  });

  /** Changing the order keeps the query, the category and the animal filter. */
  const setSort = (next: 'price' | 'newest') => {
    const updated = new URLSearchParams(params);

    // `price` is the default, so it is removed rather than written. Keeps the
    // shared URL for an ordinary search identical to what it was before the
    // control existed.
    if (next === 'price') updated.delete('sort');
    else updated.set('sort', next);

    setParams(updated, { replace: true });
  };

  /** Toggling a species keeps everything else in the URL, including the query. */
  const setSpecies = (next: SpeciesKey | undefined) => {
    const updated = new URLSearchParams(params);

    if (next) updated.set('species', next);
    else updated.delete('species');

    setParams(updated, { replace: true });
  };

  const products = data?.products ?? [];
  const total = data?.meta?.total ?? 0;

  return (
    <>
      {/* The filter control lives in the chip row below, where it reflects the
          active category. The duplicate in the bar did nothing at all. */}
      <AppBar back>
        <div className="mt-2">
          <SearchField
            autoFocus={!query}
            value={draft}
            placeholder={info.searchPlaceholder}
            onChange={(event) => setDraft(event.target.value)}
            onClear={() => setDraft('')}
          />
        </div>
      </AppBar>

      <div className="mx-auto w-full max-w-[1200px] px-4 pb-6 xl:px-6 xl:pt-6">
        {/* The marketplace banner. A buyer who arrives here from a search
            engine never sees the home screen, so without this they would never
            be told the one thing they most need to know before a rider knocks.
            Compact on purpose — it informs without shouting over the results
            they actually came for. */}
        <TrustBanner variant="compact" className="mt-3" />

        {/* Which shelf these results came from. A buyer who arrived here from a
            search engine never saw the home screen, so without this line the
            only cue that they are among animal medicines is the colour — and
            colour is never allowed to be the only cue. */}
        {isVet ? (
          <p
            data-testid="vet-search-standing"
            className="m-0 mt-3 border-y border-line py-2.5 text-[12px] leading-snug text-ink"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-blue">
              Veterinary
            </span>
            <span className="mt-0.5 block">
              Results are veterinary products only. Nothing here is for human use.
            </span>
          </p>
        ) : null}

        {debounced ? (
          <p className="pt-3 text-[11px] text-faint">
            Showing results for <b className="text-ink">{debounced}</b>
            {total > 0 ? ` · ${total} result${total === 1 ? '' : 's'}` : null}
          </p>
        ) : null}

        {/* The animal filter comes FIRST on the veterinary side, ahead of price
            and distance, because it is the one that decides whether a result is
            relevant at all: a poultry farmer and somebody with one dog share
            almost no products, and "dewormer" alone serves neither of them. */}
        {isVet ? (
          <ChipRow className="pt-3" aria-label="Animal">
            <Chip
              tone={species ? 'default' : 'selected'}
              onClick={() => setSpecies(undefined)}
            >
              All animals
            </Chip>
            {SPECIES_FILTERS.map(([key, label]) => (
              <Chip
                key={key}
                tone={species === key ? 'selected' : 'default'}
                onClick={() => setSpecies(species === key ? undefined : key)}
              >
                {label}
              </Chip>
            ))}
          </ChipRow>
        ) : null}

        {/* ────────────────── THE ORDER, SAID OUT LOUD ──────────────────
            Cheapest first is the promise the whole platform rests on, and
            until now this screen neither said so nor did it: `sort=price` was
            accepted by the API and silently ignored, so results came back in
            alphabetical order by generic name.

            These two chips are REAL controls, unlike the four decorative ones
            that used to sit beside them. "Expiry ≥ 6mo", "Delivers today",
            "Within 3 km" and "4★ and up" were `readOnly` placeholders that
            looked exactly like working filters — a buyer who pressed one and
            saw nothing happen learns the chrome is decoration, and then stops
            trusting the parts that work. They are gone until they are wired. */}
        <ChipRow className="py-3" aria-label="Order and filters">
          <Chip tone={category ? 'selected' : 'default'} icon="filter" readOnly={!category}>
            Filters{category ? ' 1' : ''}
          </Chip>
          <Chip
            tone={sort === 'price' ? 'selected' : 'default'}
            onClick={() => setSort('price')}
            data-testid="sort-price"
          >
            Lowest price
          </Chip>
          <Chip
            tone={sort === 'newest' ? 'selected' : 'default'}
            onClick={() => setSort('newest')}
            data-testid="sort-newest"
          >
            Newest
          </Chip>
        </ChipRow>

        {isError ? (
          <ErrorState
            title="Search is not responding"
            body="Your connection dropped, or ChiCare is having a moment. Neither is your fault."
            action={
              <Button full icon="refresh" onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : isLoading ? (
          <SearchSkeleton />
        ) : products.length === 0 ? (
          <EmptyState
            illustration={<NoResultsArt />}
            title={
              debounced
                ? `No seller near you has "${debounced}"`
                : isVet
                  ? 'Search animal health'
                  : 'Search for a medicine'
            }
            body={
              debounced
                ? isVet
                  ? 'Try the generic name on the pack, or clear the animal filter — many products cover several species.'
                  : 'Try the generic name printed on the pack — most brands are listed under it.'
                : isVet
                  ? 'Type the product, the condition, or what you are treating it for.'
                  : 'Type the name on the pack, or the condition you are treating.'
            }
            action={
              <Button full onClick={() => navigate(info.home)}>
                Browse categories
              </Button>
            }
            secondaryAction={
              debounced ? (
                <Button full variant="grey" onClick={() => setDraft('')}>
                  Clear search
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div
            className={`space-y-2.5 xl:grid xl:grid-cols-2 xl:gap-2.5 xl:space-y-0 ${
              isFetching ? 'opacity-60 transition-opacity' : ''
            }`}
          >
            {products.map((product) => (
              <ProductRow key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* AFTER the results, never among them. Search results are the thing
            the buyer came for and are ordered on their own merits; an
            advertisement that interleaved with them would be ChiCare selling a
            position in a list it tells people is not for sale. */}
        <SponsoredBlock placement="search" kind="product" className="mt-4" />
      </div>
    </>
  );
}

/**
 * The species filter row.
 *
 * Six, not ten. These are the animals Nigerian buyers actually shop for in
 * volume, and a row of ten chips at 393px is a row nobody reaches the end of.
 * The rest remain searchable; they are simply not chips.
 */
const SPECIES_FILTERS: ReadonlyArray<readonly [SpeciesKey, string]> = [
  ['poultry', 'Poultry'],
  ['cattle', 'Cattle'],
  ['goat', 'Goats'],
  ['dog', 'Dogs'],
  ['cat', 'Cats'],
  ['fish', 'Fish'],
] as const;

function SearchSkeleton() {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="flex gap-2.5 rounded-card border border-line bg-white p-3">
          <Skeleton className="h-[76px] w-[76px] shrink-0" radius="md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2.5 w-1/2" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Debounce.
 *
 * 250ms, not the usual 400: on a slow connection the request itself already
 * costs half a second, and stacking a long debounce on top makes the field feel
 * broken. Short enough to feel live, long enough not to fire on every keypress.
 */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);

    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
